"use client";

import type { CanvasFuseLabel } from "./canvas-fuse-label";
import { useCatalogLocale } from "@/components/hooks/use-catalog-locale";
import { NodeToolbar, Position, useUpdateNodeInternals, type NodeProps } from "@xyflow/react";
import { memo, useEffect, useRef, useState } from "react";
import { getDefinition } from "@/lib/catalog";
import { categoryColors } from "@/lib/category-style";
import { effectiveDimensions, effectiveTerminals } from "@/lib/effective";
import { getSimulateStateSpec, getStateOption } from "@/lib/simulate/states";
import { useSystemStore } from "@/lib/store";
import { useCanvasBatteryDetails, useCanvasBlueprint, useCanvasViewOnly } from "./canvas-mode";
import {
  LoadActivePills,
  SimulateOverlay,
  SimulateStatePills,
} from "./component-node-simulate-overlay";
import {
  BatteryFusePills,
  BusbarBody,
  CardBody,
  ChassisGroundBody,
  FuseboxBody,
  FuseHolderBody,
  SwitchBody,
} from "./component-node-bodies";
import { TerminalDots } from "./component-node-terminals";
import {
  activeTerminalsKey,
  useActiveTerminals,
} from "@/lib/canvas-active-terminals";
import { useCanvasHover } from "@/lib/canvas-hover";
import { commentsForNode, useCommentsStore } from "@/lib/comments/store";
import { useCommentPopover } from "@/components/comments/popover-store";
import type { ComponentDefinition, Terminal } from "@/lib/types";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import { localizedName } from "@/lib/i18n";
import { MobileDetailsPill } from "./mobile-details-pill";

export type ComponentNodeData = {
  definitionId: string;
  customDefinition?: ComponentDefinition;
  label?: string;
  /** Fuse blocks: per-slot rating, read off the connected cable's `fuseAmpOverride`. */
  terminalFuses?: Record<string, CanvasFuseLabel>;
  terminalOverrides?: Record<string, { side?: Terminal["side"]; offset?: number }>;
  terminalLabels?: Record<string, string>;
  rotation?: 0 | 90 | 180 | 270;
  cableTarget?: boolean;
  /** Busbars: stud-count override; `effective.ts` synthesizes terminals from it. */
  studCount?: number;
  /** Busbars: draw as a fusebox. Still a busbar topologically; the fuses live on the cables. */
  isFusebox?: boolean;
  /** Batteries: OCPD bolted to the +post (MRBF/MEGA/ANL/Class T). */
  batteryFuse?: { ratingA: number };
  /** Fuse holders: installed rating. Beats the cable's `fuseAmpOverride`; the store mirrors it there. */
  fuseAmp?: number | null;
};

/** True for 240 ms on the nodes selected at copy time; `index.css` pulses on the flag. */
function useJustCopiedFlash(selected: boolean): boolean {
  const lastCopyAt = useSystemStore((s) => s.lastCopyAt);
  const [flashing, setFlashing] = useState(false);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  useEffect(() => {
    if (!lastCopyAt || !selectedRef.current) return;
    setFlashing(true);
    const t = setTimeout(() => setFlashing(false), 240);
    return () => clearTimeout(t);
  }, [lastCopyAt]);
  return flashing;
}

function ComponentNodeInner({
  id,
  data,
  selected,
  positionAbsoluteX,
  positionAbsoluteY,
}: NodeProps) {
  const d = data as ComponentNodeData;
  const def = getDefinition(d.definitionId, d.customDefinition);
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    // The canvas preserves measured nodes during wire edits. Moving/adding a
    // terminal must refresh handle bounds without temporarily removing edges.
    updateNodeInternals(id);
  }, [id, def, d.terminalOverrides, d.rotation, d.studCount, d.isFusebox, updateNodeInternals]);
  const blueprint = useCanvasBlueprint();
  const showBatteryDetails = useCanvasBatteryDetails();
  const simulating = useSystemStore(
    (s) => s.mode === "simulate" && !blueprint,
  );
  const simulateStateId = useSystemStore(
    (s) => s.simulateState.componentStates[id],
  );
  const locale = useCatalogLocale();
  const setHoverNodeId = useCanvasHover((s) => s.setHoverNodeId);
  const hoverNodeId = useCanvasHover((s) => s.hoverNodeId);
  // A sorted string key, not the set: an unchanged key lets Zustand skip re-rendering this card.
  const myActiveTerminalsKey = useActiveTerminals((s) =>
    activeTerminalsKey(s, id),
  );
  const justCopied = useJustCopiedFlash(selected);
  const viewOnly = useCanvasViewOnly();
  const isTouchLayout = useIsTouchLayout();
  const allComments = useCommentsStore((s) => s.comments);
  const commentCount =
    viewOnly && !blueprint ? commentsForNode(allComments, id).length : 0;

  if (!def) {
    return (
      <div className="component-shell rounded-[12px] bg-white border border-rose-300 p-3 text-rose-700 text-xs">
        Missing component
      </div>
    );
  }

  const cat = categoryColors(def.category);
  const variant = def.renderVariant ?? "card";
  const isFuseboxMode = variant === "busbar" && !!d.isFusebox;
  const isSymbol = variant !== "card" && !isFuseboxMode;

  const terms = effectiveTerminals(def, {
    terminalOverrides: d.terminalOverrides,
    terminalLabels: d.terminalLabels,
    rotation: d.rotation,
    studCount: d.studCount,
  });
  const dims = effectiveDimensions(def, {
    terminalOverrides: d.terminalOverrides,
    rotation: d.rotation,
    studCount: d.studCount,
    isFusebox: d.isFusebox,
  });

  const rotation = d.rotation ?? 0;
  const isRotated = rotation !== 0;

  const displayName = d.label ?? localizedName(def, locale);
  const hideCardBody = simulating && showBatteryDetails && def.category === "battery" && variant === "card";
  // Not on touch: hover is what restores the dim, so a touch view would strand cards faded.
  const isOffInSimulate =
    !isTouchLayout &&
    simulating && getStateOption(def, simulateStateId)?.ampOverride === 0;

  const wrapperStyle: React.CSSProperties = isSymbol
    ? {
        width: dims.width,
        height: dims.height,
        background: "transparent",
        border: "none",
        borderRadius: 4,
        boxShadow: d.cableTarget
          ? "0 0 0 2px var(--color-accent)"
          : selected
            ? "0 0 0 1.5px var(--color-accent)"
            : "none",
        transition: "box-shadow 0.15s ease",
      }
    : {
        width: dims.width,
        height: dims.height,
        background: "var(--color-surface)",
        border: `1px solid ${d.cableTarget ? "var(--color-accent)" : "var(--color-border)"}`,
        borderRadius: 12,
        boxShadow: d.cableTarget
          ? "0 0 0 3px hsl(214 88% 52% / 0.22), var(--shadow-md)"
          : selected
            ? "var(--shadow-md)"
            : "var(--shadow-sm)",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      };

  // Recompute at rotation 0, never raw catalog dims: a grown busbar renders short and clips studs.
  const bodyDims = isRotated
    ? effectiveDimensions(def, {
        terminalOverrides: d.terminalOverrides,
        rotation: 0,
        studCount: d.studCount,
        isFusebox: d.isFusebox,
      })
    : dims;
  const bodyContainerStyle: React.CSSProperties = isRotated
    ? {
        position: "absolute",
        left: "50%",
        top: "50%",
        width: bodyDims.width,
        height: bodyDims.height,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        transformOrigin: "center center",
      }
    : { position: "absolute", inset: 0 };

  // No view-only click handler by design: it opened an empty comment popover on every card tap.

  return (
    <div
      className={`component-shell relative${isOffInSimulate ? " component-shell--off" : ""}`}
      style={wrapperStyle}
      data-just-copied={justCopied ? "true" : undefined}
      onMouseEnter={blueprint ? undefined : () => setHoverNodeId(id)}
      onMouseLeave={
        blueprint
          ? undefined
          : () =>
              setHoverNodeId(
                useCanvasHover.getState().hoverNodeId === id
                  ? null
                  : useCanvasHover.getState().hoverNodeId,
              )
      }
    >
      {variant === "card" && (
        // inset -1 pulls the clip over the 1px border; a sibling of the handles so they aren't clipped.
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: -1,
            borderRadius: 12,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 5,
              background: cat.fg,
              opacity: 0.85,
            }}
          />
        </span>
      )}
      <div style={bodyContainerStyle}>
        {variant === "card" && !hideCardBody && (
          <CardBody
            def={def}
            displayName={displayName}
            bottomSlot={
              simulating
                ? getSimulateStateSpec(def)
                  ? <SimulateStatePills nodeId={id} def={def} />
                  : def.category === "load" ? <LoadActivePills nodeId={id} /> : null
                : null
            }
          />
        )}
        {def.category === "battery" && d.batteryFuse && !simulating && (
          <BatteryFusePills
            terms={effectiveTerminals(def, { ...d, rotation: 0 })}
            ratingA={d.batteryFuse.ratingA}
            rotation={rotation}
          />
        )}
        {variant === "switch" &&
          (blueprint ? (
            <BlueprintSwitchBody displayName={displayName} />
          ) : (
            <SwitchBody nodeId={id} def={def} displayName={displayName} />
          ))}
        {variant === "fuse-holder" && (
          <FuseHolderBody
            def={def}
            fuses={d.terminalFuses}
            displayName={displayName}
            holderFuseAmp={d.fuseAmp}
          />
        )}
        {variant === "busbar" && !isFuseboxMode && (
          // Unrotated terminals: the SVG lays out unrotated and needs the synthesized studs.
          <BusbarBody
            def={def}
            displayName={displayName}
            terms={effectiveTerminals(def, { ...d, rotation: 0 })}
            dims={bodyDims}
          />
        )}
        {variant === "busbar" && isFuseboxMode && (
          <FuseboxBody
            def={def}
            terms={effectiveTerminals(def, { ...d, rotation: 0 })}
            fuses={d.terminalFuses}
            rotation={rotation}
          />
        )}
        {variant === "chassis-ground" && (
          <ChassisGroundBody displayName={displayName} />
        )}
      </div>

      <TerminalDots
        terms={terms}
        nodeId={id}
        nodeActive={blueprint || !!selected || hoverNodeId === id}
        activeTerminalsKey={myActiveTerminalsKey}
        terminalFuses={def.isFuseBlock ? d.terminalFuses : undefined}
        compact={isSymbol && variant !== "busbar"}
        positionAbsoluteX={positionAbsoluteX}
        positionAbsoluteY={positionAbsoluteY}
        nodeWidth={dims.width}
        nodeHeight={dims.height}
      />

      {!blueprint && (
        <SimulateOverlay nodeId={id} def={def} displayName={displayName} />
      )}

      {commentCount > 0 && <CommentCountChip count={commentCount} nodeId={id} />}

      {isTouchLayout && selected && !blueprint && (
        <NodeToolbar
          nodeId={id}
          isVisible
          position={Position.Bottom}
          offset={28}
          className="nodrag nopan"
          style={{ pointerEvents: "auto" }}
        >
          <MobileDetailsPill anchor={{ kind: "node", id }} />
        </NodeToolbar>
      )}
    </div>
  );
}

/** Static IEC switch: `SwitchBody`'s live simulate state and toggle don't belong on a blueprint. */
function BlueprintSwitchBody({ displayName }: { displayName: string }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 60 30"
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible", pointerEvents: "none" }}
      aria-label={displayName}
    >
      <line x1="9" y1="15" x2="20" y2="15" stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="40" y1="15" x2="51" y2="15" stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="20" y1="15" x2="40" y2="4" stroke="var(--color-fg)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="20" cy="15" r="2" fill="var(--color-fg)" />
      <circle cx="40" cy="15" r="2" fill="var(--color-fg)" />
    </svg>
  );
}

function CommentCountChip({ count, nodeId }: { count: number; nodeId: string }) {
  const toggle = useCommentPopover((s) => s.toggle);
  return (
    <button
      type="button"
      data-comment-chip
      aria-label={`${count} comment${count === 1 ? "" : "s"} — open`}
      onClick={(e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        toggle({ kind: "node", nodeId }, rect);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: -8,
        right: -8,
        zIndex: 3,
        pointerEvents: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        height: 18,
        padding: "0 6px",
        borderRadius: 9,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-strong)",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        lineHeight: 1,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        color: "var(--color-fg)",
        boxShadow: "0 1px 2px hsl(30 8% 10% / 0.06)",
        cursor: "pointer",
      }}
      className="hover:bg-[var(--color-surface-2)] transition-colors"
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      {count}
    </button>
  );
}

export const ComponentNodeView = memo(ComponentNodeInner);
